import axios from "axios";
import { ref } from "vue";
import { FirestoreService } from "./firestore";

const BASE_URL = "https://api.production.terminal.payo.com.au";

// The single bootstrap super-admin (Victor) — implicitly allowed everything and
// the only user who can open the permission page.
export const ROOT_ADMIN_UID = "HdQWpVXypmZb1Z8wOhYPNJ5ktmT2";

export type PermissionKey =
  | "storeSettings"
  | "storeDetails"
  | "settlements"
  | "orders"
  | "apps"
  | "monthlyRentalFee"
  | "admin"
  | "franchise";

export type UserPermissions = {
  uid: string;
  name?: string;
  email?: string;
  roles: Partial<Record<PermissionKey, boolean>>;
  allStores?: boolean;
  storeIds?: string[];
};

// Reactive cache of the CURRENT user's permissions, loaded once after login
// (see loadMyPermissions, called from the dashboard layout). The hardcoded
// allow-list utils delegate here, so menus/guards react to it.
export const myPermissions = ref<UserPermissions | null>(null);
export const myUid = ref<string | null>(null);

export const loadMyPermissions = async (uid: string | null | undefined) => {
  myUid.value = uid ?? null;
  if (!uid) {
    myPermissions.value = null;
    return;
  }
  try {
    myPermissions.value =
      (await FirestoreService.getDocument<UserPermissions>(
        `permissions/${uid}`,
      )) ?? null;
  } catch {
    myPermissions.value = null;
  }
};

export const isRootAdmin = (uid?: string | null): boolean =>
  (uid ?? myUid.value) === ROOT_ADMIN_UID;

// True if the current user has the permission (root admin always does).
export const can = (key: PermissionKey, uid?: string | null): boolean =>
  isRootAdmin(uid) || myPermissions.value?.roles?.[key] === true;

// Only the root admin manages permissions.
export const canManagePermissions = (uid?: string | null): boolean =>
  isRootAdmin(uid);

// Profit analytics viewers (root admin, roger, chao). Must match the Firestore
// rule on `profit-analytics`.
export const PROFIT_ANALYTICS_UIDS = new Set([
  ROOT_ADMIN_UID,
  "ALNssX7wiUgkQrM9OoE8cTBGFFw2", // roger
  "xIU2Em7l65Vw8vpiJeVuBvIDe7o1", // chao
]);
export const canViewProfitAnalytics = (uid?: string | null): boolean =>
  PROFIT_ANALYTICS_UIDS.has(uid ?? myUid.value ?? "");

// Store scope: which stores the current user may see/manage.
export const canSeeAllStores = (uid?: string | null): boolean =>
  isRootAdmin(uid) || myPermissions.value?.allStores === true;
export const allowedStoreIds = (): string[] =>
  myPermissions.value?.storeIds ?? [];

// --- Backend endpoints (root-admin only, enforced server-side) ---
export const listUserPermissions = (
  token: string,
): Promise<{ permissions: UserPermissions[] }> =>
  axios
    .post(
      `${BASE_URL}/user-permissions/list`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    )
    .then((r) => r.data);

export const setUserPermissions = (
  token: string,
  payload: UserPermissions,
): Promise<{ permissions: UserPermissions }> =>
  axios
    .post(`${BASE_URL}/user-permissions`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
    .then((r) => r.data);
